import TagSelector from '@/components/TagSelector';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

// Zod schema for contact form validation
const contactFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .trim(),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^\+?[0-9]+$/, 'Please enter a valid phone number')
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number must be less than 20 characters')
    .trim(),
  dateOfBirth: z
    .string()
    .optional()
    .refine(
      date => {
        if (!date) return true; // Optional field
        const selectedDate = new Date(date);
        const today = new Date();
        return selectedDate <= today;
      },
      {
        message: 'Date of birth cannot be in the future',
      }
    ),
  tags: z.array(z.any()).default([]),
});

/**
 * Project Contact Form Component - Form for creating and editing project contacts
 * @param {Object} props - Component props
 * @param {Object} props.contact - Contact data for editing (optional)
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isLoading - Loading state
 * @returns {JSX.Element} Project contact form component
 */
const ProjectContactForm = ({
  contact,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      phoneNumber: '',
      dateOfBirth: '',
      tags: [],
    },
  });

  // Initialize form data when contact prop changes (for editing)
  useEffect(() => {
    if (contact) {
      setValue('name', contact.name || '');
      // Strip 91 or +91 prefix for display given the static +91 UI prefix
      let displayPhone = contact.phoneNumber || '';
      if (displayPhone.startsWith('+91')) {
        displayPhone = displayPhone.substring(3);
      } else if (displayPhone.startsWith('91')) {
        displayPhone = displayPhone.substring(2);
      }
      setValue('phoneNumber', displayPhone);
      setValue(
        'dateOfBirth',
        contact.dateOfBirth ? contact.dateOfBirth.split('T')[0] : ''
      );
      setValue('tags', contact.tags || []);
    } else {
      reset();
    }
  }, [contact, setValue, reset]);

  // Handle form submission
  const onFormSubmit = data => {
    // Transform data to match API schema
    // Handle phone number: prepend 91 if needed, remove + sign
    let phoneValue = data.phoneNumber.trim();
    if (phoneValue.length > 10) {
      if (phoneValue.startsWith('+91')) {
        phoneValue = phoneValue.substring(3);
      } else if (phoneValue.startsWith('91')) {
        phoneValue = phoneValue.substring(2);
      }
    }
    // Remove any remaining + or non-digit characters just in case
    phoneValue = phoneValue.replace(/\+/g, '').replace(/\D/g, '');
    const finalPhone = `91${phoneValue}`;

    const submitData = {
      name: data.name.trim(),
      phoneNumber: finalPhone,
      ...(data.dateOfBirth && { dateOfBirth: data.dateOfBirth }),
      tags: data.tags.map(tag =>
        typeof tag === 'string' ? tag : tag._id || tag.name
      ),
    };

    onSubmit(submitData);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {contact ? 'Edit Contact' : 'Add New Contact'}
        </h2>
        <p className="text-sm text-gray-500">
          {contact
            ? 'Update contact information'
            : 'Enter contact details for this project'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
        {/* Name Field */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            {...register('name')}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter full name"
            disabled={isLoading || isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Phone Number Field */}
        <div>
          <label
            htmlFor="phoneNumber"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="flex">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm font-medium">
              +91
            </span>
            <input
              type="tel"
              id="phoneNumber"
              {...register('phoneNumber')}
              className={`flex-1 block w-full px-3 py-2 border rounded-none rounded-r-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                errors.phoneNumber ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="9876543210"
              disabled={isLoading || isSubmitting}
            />
          </div>
          {errors.phoneNumber && (
            <p className="mt-1 text-sm text-red-600">
              {errors.phoneNumber.message}
            </p>
          )}
        </div>

        {/* Date of Birth Field */}
        <div>
          <label
            htmlFor="dateOfBirth"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Date of Birth
          </label>
          <input
            type="date"
            id="dateOfBirth"
            {...register('dateOfBirth')}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={isLoading || isSubmitting}
          />
          {errors.dateOfBirth && (
            <p className="mt-1 text-sm text-red-600">
              {errors.dateOfBirth.message}
            </p>
          )}
        </div>

        {/* Tags Selection */}
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <TagSelector
              selectedTags={field.value}
              onChange={field.onChange}
              isLoading={isLoading || isSubmitting}
            />
          )}
        />

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            disabled={isLoading || isSubmitting}
          >
            {isLoading || isSubmitting ? (
              <div className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {contact ? 'Updating...' : 'Creating...'}
              </div>
            ) : contact ? (
              'Update Contact'
            ) : (
              'Create Contact'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectContactForm;
